import React, { useCallback, useRef, useState } from "react";

import Cropper, { Area, MediaSize } from "react-easy-crop";
import { toast } from "react-toastify";
import {
  LuCamera,
  LuCrop,
  LuImage,
  LuImagePlus,
  LuUpload,
  LuX,
  LuZoomIn,
  LuZoomOut,
} from "react-icons/lu";

import Button from "../ui/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
} from "../ui/styles";
import { toastStyles } from "../../static/styles";

export type AspectOption = {
  label: string;
  value: number;
};

export type OutputBox = {
  width: number;
  height: number;
};

export type OutputType = "image/png" | "image/jpeg";

export const originalAspect = 0;

type Props = {
  label: string;
  value: string | null;
  existingImageUrl?: string | null;
  onChange: (dataUrl: string | null) => void;
  aspectOptions: AspectOption[];
  outputBox: OutputBox;
  hasError?: boolean;
  allowCamera?: boolean;
  outputType?: OutputType;
  maxFileSizeMb?: number;
};

const acceptedTypes = ["image/png", "image/jpeg", "image/webp"];
const jpegQuality = 0.7;
const minZoom = 1;
const maxZoom = 3;

const pickButtonClass =
  "inline-flex items-center gap-1.5 rounded-lg border-2 border-[#113F67]/30 bg-white px-4 py-2 text-sm font-medium text-[#113F67] transition-colors hover:border-[#113F67] hover:bg-[#113F67]/5";

const previewLinkClass =
  "inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-[#113F67] hover:text-[#0d2f4d] font-medium";

const checkerboardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  backgroundImage:
    "linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
};

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area,
  outputWidth: number,
  outputHeight: number,
  outputType: OutputType
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) throw new Error("No 2d context");

  canvas.width = outputWidth;
  canvas.height = outputHeight;
  context.imageSmoothingQuality = "high";

  if (outputType === "image/jpeg") {
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, outputWidth, outputHeight);
  }

  context.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  return canvas.toDataURL(outputType, jpegQuality);
};

const ImageCropUpload = ({
  label,
  value,
  existingImageUrl,
  onChange,
  aspectOptions,
  outputBox,
  hasError,
  allowCamera = false,
  outputType = "image/png",
  maxFileSizeMb = 5,
}: Props) => {
  const [rawImage, setRawImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(minZoom);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(
    null
  );
  const [activeAspect, setActiveAspect] = useState<number>(
    aspectOptions[0].value
  );
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [mediaAspect, setMediaAspect] = useState<number>(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const displayImage = value || existingImageUrl || null;
  const zoomPercent = ((zoom - minZoom) / (maxZoom - minZoom)) * 100;
  const cropAspect = activeAspect || mediaAspect;

  const openFilePicker = () => fileInputRef.current?.click();

  const openCamera = () => cameraInputRef.current?.click();

  const onSelectFile = (file: File) => {
    if (!acceptedTypes.includes(file.type)) {
      toast.error("માત્ર PNG, JPG અથવા WEBP ઇમેજ પસંદ કરો.", {
        ...(toastStyles as any),
      });
      return;
    }

    if (file.size > maxFileSizeMb * 1024 * 1024) {
      toast.error(`ઇમેજ ${maxFileSizeMb} MB થી નાની હોવી જોઈએ.`, {
        ...(toastStyles as any),
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCrop({ x: 0, y: 0 });
      setZoom(minZoom);
      setCroppedAreaPixels(null);
      setActiveAspect(aspectOptions[0].value);
      setRawImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) onSelectFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDropZoneKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onSelectFile(file);
    event.target.value = "";
  };

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const onMediaLoaded = useCallback((mediaSize: MediaSize) => {
    setMediaAspect(
      mediaSize.naturalHeight
        ? mediaSize.naturalWidth / mediaSize.naturalHeight
        : 1
    );
  }, []);

  const closeCropDialog = useCallback(() => {
    setRawImage(null);
  }, []);

  const handleApply = async () => {
    if (!rawImage || !croppedAreaPixels) return;

    setIsApplying(true);

    try {
      const boxHeight = Math.min(outputBox.width / cropAspect, outputBox.height);
      const outputHeight =
        outputType === "image/jpeg"
          ? Math.min(boxHeight, croppedAreaPixels.height)
          : boxHeight;
      const dataUrl = await getCroppedImg(
        rawImage,
        croppedAreaPixels,
        Math.round(outputHeight * cropAspect),
        Math.round(outputHeight),
        outputType
      );
      onChange(dataUrl);
      setRawImage(null);
    } catch (error) {
      toast.error("ઇમેજ ક્રોપ થઈ શકી નહીં, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsApplying(false);
  };

  return (
    <div className="space-y-2">
      {displayImage ? (
        <div
          className={cx(
            "flex items-center gap-4 p-3 rounded-lg border",
            allowCamera ? "bg-white" : "bg-gray-50",
            hasError ? "border-red-500" : "border-gray-200"
          )}
        >
          <div
            className="h-20 min-w-0 max-w-[16rem] flex-1 overflow-hidden rounded-lg border border-gray-200"
            style={outputType === "image/png" ? checkerboardStyle : undefined}
          >
            <img
              src={displayImage}
              alt={label}
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex flex-shrink-0 flex-col gap-2">
            <button
              type="button"
              onClick={openFilePicker}
              className={previewLinkClass}
            >
              <LuImagePlus className="w-4 h-4" />
              {label} બદલો
            </button>
            {allowCamera && (
              <button
                type="button"
                onClick={openCamera}
                className={previewLinkClass}
              >
                <LuCamera className="w-4 h-4" />
                કેમેરા
              </button>
            )}
            <button
              type="button"
              onClick={() => onChange(null)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-red-500 hover:text-red-600 font-medium"
            >
              <LuX className="w-4 h-4" />
              દૂર કરો
            </button>
          </div>
        </div>
      ) : allowCamera ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragOver(false)}
          className={cx(
            "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
            isDragOver
              ? "border-[#113F67] bg-[#113F67]/5"
              : hasError
              ? "border-red-500 bg-white"
              : "border-gray-300 bg-white"
          )}
        >
          <LuCamera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            {label} પાડો અથવા ગેલેરીમાંથી પસંદ કરો
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={openCamera}
              className={pickButtonClass}
            >
              <LuCamera className="w-4 h-4" />
              કેમેરા
            </button>
            <button
              type="button"
              onClick={openFilePicker}
              className={pickButtonClass}
            >
              <LuImage className="w-4 h-4" />
              ગેલેરી
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            PNG, JPG, WEBP · {maxFileSizeMb} MB સુધી
          </p>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={openFilePicker}
          onKeyDown={handleDropZoneKeyDown}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragOver(false)}
          className={cx(
            "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] focus-visible:ring-offset-2",
            isDragOver
              ? "border-[#113F67] bg-[#113F67]/5"
              : hasError
              ? "border-red-500 hover:bg-gray-50"
              : "border-gray-300 hover:border-[#113F67] hover:bg-gray-50"
          )}
        >
          <LuUpload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            {label} અહીં ડ્રોપ કરો અથવા પસંદ કરવા ક્લિક કરો
          </p>
          <p className="text-xs text-gray-400 mt-1">
            PNG, JPG, WEBP · {maxFileSizeMb} MB સુધી
          </p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(",")}
        onChange={handleFileChange}
        className="hidden"
      />

      {allowCamera && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="hidden"
        />
      )}

      <Dialog open={rawImage !== null} onClose={closeCropDialog}>
        <DialogContent
          size="lg"
          className={cx(
            "font-inter max-h-[90vh] overflow-y-auto",
            dialogMobileContentClass
          )}
        >
          <DialogHeader className={dialogMobileHeaderClass}>
            <h2
              className={cx(
                dialogTitleClass,
                "flex items-center justify-center gap-2 sm:justify-start"
              )}
            >
              <LuCrop className="h-5 w-5" />
              {label} ક્રોપ કરો
            </h2>
            <p className="text-sm text-gray-500">
              ક્રોપ એરિયા અને ઝૂમ ગોઠવીને {label} સેટ કરો.
            </p>
          </DialogHeader>

          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            {aspectOptions.length > 1 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-[#113F67]">
                  {label}નો આકાર
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {aspectOptions.map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setActiveAspect(option.value)}
                      className={cx(
                        "px-3 py-1.5 rounded-lg text-sm border transition-colors",
                        activeAspect === option.value
                          ? "bg-[#113F67] text-white border-[#113F67]"
                          : "bg-white text-[#113F67] border-gray-300 hover:bg-gray-50"
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div
              className="relative w-full h-64 sm:h-80 rounded-lg overflow-hidden"
              style={checkerboardStyle}
            >
              {rawImage && (
                <Cropper
                  image={rawImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={cropAspect}
                  minZoom={minZoom}
                  maxZoom={maxZoom}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  onMediaLoaded={onMediaLoaded}
                />
              )}
            </div>

            <div className="flex items-center gap-3 px-1">
              <LuZoomOut className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div className="flex-1 relative h-10 flex items-center">
                <div className="absolute inset-x-0 h-1.5 bg-gray-200 rounded-full" />
                <div
                  className="absolute left-0 h-1.5 bg-[#113F67] rounded-full"
                  style={{ width: `${zoomPercent}%` }}
                />
                <input
                  type="range"
                  min={minZoom}
                  max={maxZoom}
                  step={0.05}
                  value={zoom}
                  aria-label="ઝૂમ"
                  onChange={(event) => setZoom(Number(event.target.value))}
                  className="absolute inset-x-0 w-full h-10 opacity-0 cursor-pointer"
                />
                <div
                  className="absolute w-5 h-5 bg-[#113F67] rounded-full shadow-md border-2 border-white pointer-events-none"
                  style={{ left: `calc(${zoomPercent}% - 10px)` }}
                />
              </div>
              <LuZoomIn className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={closeCropDialog}
              disabled={isApplying}
            >
              રદ કરો
            </Button>

            <Button
              type="button"
              variant="primary"
              className="flex-1"
              onClick={handleApply}
              disabled={isApplying || !croppedAreaPixels}
            >
              {isApplying ? (
                <span className="flex items-center">
                  <span className={buttonSpinnerClass}></span>
                  લાગુ થઈ રહ્યું છે...
                </span>
              ) : (
                "લાગુ કરો"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageCropUpload;
